<template>
  <div
    class="message admin"
    :class="[
      { mine: message.user.id === user.id },
      { system: message.type === 'system' },
    ]"
  >
    <!-- SYSTEM MESSAGE -->
    <template v-if="message.type === 'system'">
      <div class="system-message" :id="`messages-${message.id}`">
        <div class="system-header">
          <span class="system-icon">📢</span>
          <span class="system-title">
            {{ message.title || "System Announcement" }}
          </span>
        </div>

        <div class="system-content" v-html="decodeMessage(message.message)" />

        <span class="time">{{ timeString }}</span>
      </div>
    </template>

    <!-- REGULAR MESSAGE -->
    <template v-else>
      <!-- avatar -->
      <div class="avatar">
        <img
          v-if="isPrevMessageUserSame"
          :src="message.user.avatar"
          alt="avatar"
        />
      </div>

      <div class="message-body" :id="`messages-${message.id}`">
        <!-- sender -->
        <p
          v-if="isLastMessageFromSameUser && user.id !== message.user.id"
          class="sender"
        >
          {{ message.user.name }}
        </p>

        <!-- admin actions -->
        <div class="admin-actions">
          <Tooltip text="Delete message">
            <button @click="$emit('delete', message)">🗑</button>
          </Tooltip>

          <Tooltip text="Mute user">
            <button @click="$emit('mute', message.user)">🔇</button>
          </Tooltip>

          <Tooltip text="Block user">
            <button @click="$emit('block', message.user)">⛔</button>
          </Tooltip>
        </div>

        <!-- message -->
        <div class="message-content">
          <p
            v-html="decodeMessage(message.message)"
            :class="{
              'message-own': user.id === message.user.id,
            }"
            class="message-over"
          />
        </div>

        <span class="time">{{ timeString }}</span>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue"
import { MessageType, UserType } from "../../types"
import Tooltip from "@/components/uikit/Tooltip.vue"

export default defineComponent({
  name: "MessageComponent",
  components: { Tooltip },
  props: {
    message: {
      type: Object as PropType<MessageType>,
      required: true,
    },
    user: {
      type: Object as PropType<UserType>,
      required: true,
    },
    isPrevMessageUserSame: Boolean,
    isLastMessageFromSameUser: Boolean,
  },

  emits: ["delete", "mute", "block", "pin"],

  computed: {
    timeString() {
      const date = new Date(Number(this.message.created_at))

      return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`
    },
  },

  methods: {
    decodeMessage(message: string): string {
      if (!message) return ""

      const temp = document.createElement("div")
      temp.innerHTML = message
      const messageContent = temp.textContent || temp.innerText || ""

      const escaped = messageContent
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")

      const mentions = escaped.replace(
        /@(\w+)/g,
        `<span class="mention-item">@$1</span>`
      )

      return mentions.replace(/\n/g, "<br>")
    },
  },
})
</script>
<style lang="scss" scoped>
@import "../../index.scss";
.message {
  position: relative;

  .admin-actions {
    position: absolute;
    top: 4px;
    right: 8px;
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: 0.2s;

    button {
      background: rgba(0, 0, 0, 0.4);
      border: none;
      color: white;
      cursor: pointer;
      border-radius: 6px;
      padding: 2px 6px;
      font-size: 12px;
    }
  }

  &:hover .admin-actions {
    opacity: 1;
  }

  &.system {
    display: flex;
    justify-content: center;
    margin: 12px 0;

    .message-body {
      background: rgba(255, 215, 0, 0.08);
      border: 1px solid rgba(255, 215, 0, 0.2);
    }
  }
}
</style>
